import React from "react";
import { Link } from "react-router-dom";
import { services } from "../../data/services";
import {
  Facebook,
  Instagram,
  Linkedin,
  Twitter,
  Mail,
  MapPin,
  Phone,
} from "lucide-react";
import { companyDetails, logo } from "../../data/constant";

const Footer = () => {
  return (
    <footer className="bg-slate-50 dark:bg-[#050505] pt-20 pb-10 border-t border-slate-200 dark:border-white/5 relative overflow-hidden">
      {/* Decorative Elements */}
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary via-purpleColor to-secondary"></div>
      <div className="absolute -top-24 -right-24 w-96 h-96 bg-primary/5 rounded-full blur-3xl"></div>
      <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-secondary/5 rounded-full blur-3xl"></div>

      <div className="wrapper relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8 mb-16">
          {/* Brand Column */}
          <div className="space-y-6">
            <Link to="/" className="inline-block" aria-label="PANTHM AI Labs Home">
              <img 
                src={logo} 
                alt="PANTHM Logo" 
                width={180}
                height={48}
                className="h-12 object-contain dark:invert dark:hue-rotate-180 transition-all duration-300" 
              />
            </Link>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed text-sm">
              Building intelligent products that redefine industries.
            </p>
            <p className="text-xs text-slate-400 dark:text-slate-500 leading-relaxed border-l-2 border-slate-200 dark:border-white/10 pl-4 mt-2">
              PANTHM AI Labs is an independent AI software development and automation company based in Pune, India having a leading global client base. We build custom AI solutions, automation systems, and digital products for businesses worldwide.
            </p>
            <div className="flex gap-4">
              {[
                { Icon: Linkedin, link: "https://www.linkedin.com/company/110580934/", label: "PANTHM LinkedIn Profile" },
                { Icon: Instagram, link: "/", label: "PANTHM Instagram Profile" },
                { Icon: Facebook, link: "/", label: "PANTHM Facebook Profile" },
                { Icon: Twitter, link: "/", label: "PANTHM Twitter Profile" }
              ].map((item, i) => (
                <a
                  key={i}
                  href={item.link}
                  target={item.link.startsWith("http") ? "_blank" : "_self"}
                  rel={item.link.startsWith("http") ? "noopener noreferrer" : undefined}
                  className="w-10 h-10 flex items-center justify-center rounded-full bg-white dark:bg-white/5 border border-slate-200 dark:border-white/10 text-slate-500 dark:text-slate-400 hover:bg-primary hover:text-white dark:hover:bg-primary dark:hover:text-white dark:hover:border-primary hover:border-primary transition-all duration-300 shadow-sm"
                  aria-label={item.label}
                >
                  <item.Icon size={18} />
                </a>
              ))}
            </div>
          </div>

          {/* AI Workforce Column */}
          <div>
            <h4 className="text-lg font-bold text-slate-900 dark:text-white mb-6">AI Employees</h4>
            <ul className="space-y-3.5 text-sm">
              {[
                { name: "AI Sales SDR", path: "/ai-employees/sales-sdr" },
                { name: "AI Customer Support", path: "/ai-employees/customer-support" },
                { name: "AI Receptionist & Concierge", path: "/ai-employees/receptionist-concierge" },
                { name: "AI Operations Manager", path: "/ai-employees/operations-manager" },
                { name: "AI Executive Assistant", path: "/ai-employees/executive-assistant" },
                { name: "Solutions Directory", path: "/solutions" },
              ].map((role, index) => (
                <li key={index}>
                  <Link
                    to={role.path}
                    className="text-slate-600 dark:text-slate-400 hover:text-primary dark:hover:text-primary transition-colors flex items-center gap-2 group"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-slate-300 group-hover:bg-primary transition-colors"></span>
                    {role.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services Column */}
          <div>
            <h4 className="text-lg font-bold text-slate-900 dark:text-white mb-6">Expertise</h4>
            <ul className="space-y-3.5 text-sm">
              {services.slice(0, 5).map((service, index) => {
                const slugify = (text) => text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)+/g, '');
                return (
                  <li key={index}>
                    <Link
                      to={`/services/${slugify(service.title)}`}
                      className="text-slate-600 dark:text-slate-400 hover:text-primary dark:hover:text-primary transition-colors flex items-center gap-2 group"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-slate-300 group-hover:bg-primary transition-colors"></span>
                      {service.title}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Quick Links Column */}
          <div>
            <h4 className="text-lg font-bold text-slate-900 dark:text-white mb-6">Company</h4>
            <ul className="space-y-3.5 text-sm">
              {[
                { name: "About Us", path: "/about-us" },
                { name: "Our Work", path: "/portfolio" },
                { name: "Trust & Security", path: "/security" },
                { name: "Insights", path: "/blogs" },
                { name: "AI ROI Calculator", path: "/tools/ai-roi-calculator" },
                { name: "Contact", path: "/contact" },
              ].map((link, index) => (
                <li key={index}>
                  <Link
                    to={link.path}
                    className="text-slate-600 dark:text-slate-400 hover:text-primary dark:hover:text-primary transition-colors flex items-center gap-2 group"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-slate-300 group-hover:bg-primary transition-colors"></span>
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Column */}
          <div>
            <h4 className="text-lg font-bold text-slate-900 dark:text-white mb-6">
              Get in Touch
            </h4>
            <ul className="space-y-6">
              <li className="flex items-start gap-4">
                <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-full bg-primary/10 text-primary">
                  <MapPin size={20} />
                </div>
                <p className="text-slate-600 dark:text-slate-400 text-sm leading-relaxed">
                  {companyDetails.address}
                </p>
              </li>
              <li className="flex items-center gap-4">
                <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-full bg-primary/10 text-primary">
                  <Phone size={20} />
                </div>
                <a
                  href={`tel:${companyDetails.phone}`}
                  className="text-slate-600 dark:text-slate-400 hover:text-primary dark:hover:text-primary transition-colors font-medium"
                >
                  {companyDetails.phone}
                </a>
              </li>
              <li className="flex items-center gap-4">
                <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-full bg-primary/10 text-primary">
                  <Mail size={20} />
                </div>
                <a
                  href={`mailto:${companyDetails.email}`}
                  className="text-slate-600 dark:text-slate-400 hover:text-primary dark:hover:text-primary transition-colors font-medium"
                >
                  {companyDetails.email}
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-slate-200 dark:border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-slate-500 text-sm">
            &copy; {new Date().getFullYear()} PANTHM AI Labs. All rights reserved.
          </p>
          <div className="flex flex-wrap gap-6 text-sm">
            <Link to="/privacy-policy" className="text-slate-500 hover:text-primary transition-colors">
              Privacy Policy
            </Link>
            <Link to="/security" className="text-slate-500 hover:text-primary transition-colors">
              Trust & Security
            </Link>
            <Link to="/terms" className="text-slate-500 hover:text-primary transition-colors">
              Terms of Service
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
